import { useEffect, useMemo, useState } from 'react';
import { CalendarRange, Check, Clock, Plus, Undo2, X } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DatePicker } from '@/components/ui/DatePicker';
import { EmptyState } from '@/components/ui/EmptyState';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { Table, type Column } from '@/components/ui/Table';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { ApiRequestError } from '@/lib/apiClient';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { StaffLeaveDecision, StaffLeaveRequest, StaffLeaveSession, StaffLeaveStatus } from '@/types/staffLeave';
import { staffRecordApi } from '../api/staffLeave.api';
import {
  STAFF_LEAVE_BAR,
  STAFF_LEAVE_HINT,
  STAFF_LEAVE_LABEL,
  STAFF_LEAVE_STATUS_LABEL,
  STAFF_LEAVE_STATUS_TONE,
} from '../constants';
import type { StaffLeavePayload } from '../schemas/leave.schema';
import { StaffLeaveModal } from './StaffLeaveModal';

export interface StaffLeaveTabProps {
  staffUuid: string;
  staffName: string;
}

/**
 * Where a leave panel reads and writes — the office's calls about somebody
 * else, or a person's own. Passed in rather than imported here, so each panel's
 * network calls stay in its own feature's `api/` folder (CLAUDE.md §6).
 */
export interface LeaveSource {
  /** Changes whenever the record being shown changes, so the panel reloads. */
  key: string;
  /** The current session's record, or `academicYearUuid`'s when a past one is chosen. */
  load: (academicYearUuid?: string) => Promise<StaffLeaveSession>;
  apply: (payload: StaffLeavePayload) => Promise<unknown>;
  withdraw: (leaveUuid: string) => Promise<unknown>;
  /** The office's only. Without it there are no approve or refuse buttons. */
  decide?: (leaveUuid: string, status: StaffLeaveDecision) => Promise<unknown>;
}

export interface LeavePanelProps {
  source: LeaveSource;
  /** Who the record is about, for the wording. Ignored when `isOwn`. */
  staffName: string;
  /**
   * The person is looking at their own leave: they apply rather than record,
   * and may withdraw only what the office has not decided yet — the server
   * holds them to the same rule.
   */
  isOwn?: boolean;
  /** Controlled state for opening the apply/record leave modal. */
  isFormOpen?: boolean;
  /** Callback when the modal open state changes. */
  onFormOpenChange?: (open: boolean) => void;
  yearUuid?: string;
  onYearChange?: (yearUuid: string) => void;
  onAcademicYearsLoaded?: (years: { uuid: string; name: string; isCurrent: boolean }[]) => void;
  hideYearSelector?: boolean;
}

/**
 * One member of staff's leave for a session — the current one unless a past
 * one is chosen.
 *
 * The balance is the institute's entitlement minus the approved days, summed by
 * the server on every read rather than stored — so withdrawing or refusing an
 * application moves the card the moment it happens, and the two can never
 * drift apart.
 *
 * A past session is for reading: applying always goes to the current one, which
 * the server decides, so the button waits until the current year is showing.
 */
export function StaffLeaveTab({ staffUuid, staffName }: StaffLeaveTabProps) {
  const source: LeaveSource = {
    key: staffUuid,
    load: (academicYearUuid) => staffRecordApi.leave(staffUuid, academicYearUuid),
    apply: (payload) => staffRecordApi.apply(staffUuid, payload),
    withdraw: (leaveUuid) => staffRecordApi.cancel(leaveUuid),
    decide: (leaveUuid, status) => staffRecordApi.decide(leaveUuid, status),
  };
  return <LeavePanel source={source} staffName={staffName} />;
}

type LeaveStatusFilter = 'ALL' | StaffLeaveStatus;

/**
 * The log's filters, as they sit in the address bar, so stepping away from the
 * leave tab and coming back finds the same session, status and dates.
 *
 * Prefixed: the panel sits inside a staff profile and a teacher's attendance
 * screen, whose own filters must not read these. `year` is used only when the
 * screen around the panel does not choose the session itself; '' is the
 * current session — the server's default, and where applying happens.
 */
type Filters = {
  year: string;
  status: LeaveStatusFilter;
  from: string;
  to: string;
};

const DEFAULT_FILTERS: Filters = {
  year: '',
  status: 'PENDING',
  from: '',
  to: '',
};

const FILTER_OPTIONS = {
  prefix: 'leave',
  allowed: { status: ['ALL', ...Object.keys(STAFF_LEAVE_STATUS_LABEL)] },
};

/** Whether an application touches the chosen window. Either end may be open. */
function overlaps(request: StaffLeaveRequest, from: string, to: string): boolean {
  return (!from || request.endDate >= from) && (!to || request.startDate <= to);
}

/** The balance cards, the log and the form — for the office or for the person themselves. */
export function LeavePanel({
  source,
  staffName,
  isOwn = false,
  isFormOpen: controlledFormOpen,
  onFormOpenChange,
  yearUuid: controlledYearUuid,
  onYearChange,
  onAcademicYearsLoaded,
  hideYearSelector = false,
}: LeavePanelProps) {
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const { from, to, status: statusFilter } = filters;
  const yearUuid = controlledYearUuid !== undefined ? controlledYearUuid : filters.year;

  const { data, isLoading, error, refetch } = useApiQuery(
    () => source.load(yearUuid || undefined),
    [source.key, yearUuid],
  );

  useEffect(() => {
    if (data?.academicYears && onAcademicYearsLoaded) {
      onAcademicYearsLoaded(data.academicYears);
    }
  }, [data?.academicYears, onAcademicYearsLoaded]);
  const recordLabel = isOwn ? 'Apply for leave' : 'Record leave';
  const isPastYear = Boolean(data && !data.academicYear.isCurrent);

  const [internalFormOpen, setInternalFormOpen] = useState(false);
  const isFormOpen = controlledFormOpen !== undefined ? controlledFormOpen : internalFormOpen;
  const setIsFormOpen = (open: boolean) => {
    if (onFormOpenChange) {
      onFormOpenChange(open);
    } else {
      setInternalFormOpen(open);
    }
  };
  /** Held rather than withdrawn on the spot — the confirmation names what goes. */
  const [withdrawing, setWithdrawing] = useState<StaffLeaveRequest | null>(null);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  /** The row a decision is in flight on, so only its own buttons go quiet. */
  const [decidingUuid, setDecidingUuid] = useState<string | null>(null);

  /**
   * The log narrowed to the dates and status chosen.
   */
  const dateFilteredRequests = useMemo(
    () => (data?.requests ?? []).filter((request) => overlaps(request, from, to)),
    [data, from, to],
  );
  const hasDateFilter = Boolean(from || to);

  const counts = useMemo(() => {
    const tally = { ALL: 0, PENDING: 0, APPROVED: 0, REJECTED: 0, CANCELLED: 0 };
    for (const request of dateFilteredRequests) {
      tally.ALL += 1;
      if (request.status in tally) tally[request.status as keyof typeof tally] += 1;
    }
    return tally;
  }, [dateFilteredRequests]);

  const requests = useMemo(() => {
    if (statusFilter === 'ALL') return dateFilteredRequests;
    return dateFilteredRequests.filter((request) => request.status === statusFilter);
  }, [dateFilteredRequests, statusFilter]);

  function chooseYear(next: string) {
    // Dates from one session mean nothing in another.
    const cleared: Partial<Filters> = { from: '', to: '', status: 'ALL' };
    if (onYearChange) {
      onYearChange(next);
      setFilters(cleared);
    } else {
      setFilters({ ...cleared, year: next });
    }
  }

  async function handleSave(payload: StaffLeavePayload) {
    try {
      await source.apply(payload);
      toast.success(
        isOwn ? 'Leave request sent' : 'Leave recorded',
        isOwn
          ? 'The school office will approve or refuse it, and you will be told either way.'
          : 'It is waiting for a decision and is not counted against the balance yet.',
      );
      refetch();
    } catch (caught) {
      throw caught instanceof ApiRequestError
        ? new Error(caught.message)
        : new Error('Something went wrong. Please try again.');
    }
  }

  async function decide(request: StaffLeaveRequest, status: StaffLeaveDecision) {
    if (!source.decide) return;
    setDecidingUuid(request.uuid);
    try {
      await source.decide(request.uuid, status);
      toast.success(
        status === 'APPROVED' ? 'Leave approved' : 'Leave refused',
        `${staffName} has been told.`,
      );
      refetch();
    } catch (caught) {
      toast.error(
        'Could not save that decision',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setDecidingUuid(null);
    }
  }

  async function confirmWithdraw() {
    if (!withdrawing) return;

    setIsWithdrawing(true);
    try {
      await source.withdraw(withdrawing.uuid);
      toast.success('Leave withdrawn', 'It stays on the log, marked as withdrawn.');
      setWithdrawing(null);
      refetch();
    } catch (caught) {
      toast.error(
        'Could not withdraw that request',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsWithdrawing(false);
    }
  }

  const columns: Column<StaffLeaveRequest>[] = [
    {
      key: 'dates',
      header: 'Dates',
      render: (request) => (
        <div className="min-w-0">
          <p className="whitespace-nowrap font-[560] text-ink">
            {formatDate(request.startDate)}
            {request.endDate !== request.startDate && ` – ${formatDate(request.endDate)}`}
          </p>
          <p className="text-xs text-ink-3">
            {request.days} {request.days === 1 ? 'day' : 'days'}
          </p>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Leave type',
      render: (request) => (
        <span className="inline-flex items-center gap-2 text-ink-2">
          <span className={cn('h-2 w-2 shrink-0 rounded-full', STAFF_LEAVE_BAR[request.type])} aria-hidden />
          {STAFF_LEAVE_LABEL[request.type]}
        </span>
      ),
    },
    {
      key: 'reason',
      header: 'Reason',
      hideOnMobile: true,
      render: (request) => (
        <div className="flex flex-col gap-1 max-w-[280px]">
          <p className="line-clamp-2 break-words text-ink-2" title={request.reason}>
            {request.reason}
          </p>
         
        </div>
      ),
    },
    {
      key: 'applied',
      header: 'Applied on',
      hideOnMobile: true,
      render: (request) => (
        <span className="whitespace-nowrap text-ink-3">{formatDate(request.appliedOn)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      align: 'right',
      render: (request) => {
        const isBusy = decidingUuid === request.uuid;
        const canWithdrawOwn = isOwn && request.status === 'PENDING';
        const canDecide = !isOwn && Boolean(source.decide) && request.status !== 'CANCELLED';

        return (
          <div className="flex flex-col items-end gap-1">
            <div className="flex flex-wrap items-center justify-end gap-1.5">
              <Badge tone={STAFF_LEAVE_STATUS_TONE[request.status]}>
                {STAFF_LEAVE_STATUS_LABEL[request.status]}
              </Badge>

              {/* Their own: withdraw only, and only while it waits — undoing a
                  decision is the office's. */}
              {canWithdrawOwn && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  disabled={isBusy}
                  onClick={() => setWithdrawing(request)}
                  aria-label={`Withdraw the leave from ${formatDate(request.startDate)}`}
                  title="Withdraw this request"
                >
                  <Undo2 className="h-3.5 w-3.5" aria-hidden />
                </Button>
              )}

              {/* Deciding again is how a mistake is undone, so the buttons stay
                  on a decided row. Only a withdrawn one is closed. */}
              {canDecide && (
                <>
                  {request.status !== 'APPROVED' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      iconOnly
                      disabled={isBusy}
                      onClick={() => decide(request, 'APPROVED')}
                      aria-label={`Approve the leave from ${formatDate(request.startDate)}`}
                      title="Approve"
                    >
                      <Check className="h-3.5 w-3.5" aria-hidden />
                    </Button>
                  )}
                  {request.status !== 'REJECTED' && (
                    <Button
                      variant="dangerSoft"
                      size="sm"
                      iconOnly
                      disabled={isBusy}
                      onClick={() => decide(request, 'REJECTED')}
                      aria-label={`Refuse the leave from ${formatDate(request.startDate)}`}
                      title="Refuse"
                    >
                      <X className="h-3.5 w-3.5" aria-hidden />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    disabled={isBusy}
                    onClick={() => setWithdrawing(request)}
                    aria-label={`Withdraw the leave from ${formatDate(request.startDate)}`}
                    title="Withdraw"
                  >
                    <Undo2 className="h-3.5 w-3.5" aria-hidden />
                  </Button>
                </>
              )}
            </div>

            {request.reviewedBy && (
              <p className="text-xs text-ink-3">by {request.reviewedBy}</p>
            )}
            {request.decisionNote && (
              <p
                className={cn(
                  'max-w-[240px] break-words text-right text-xs',
                  request.status === 'REJECTED'
                    ? 'font-semibold text-rose-600 dark:text-rose-400'
                    : 'text-ink-2',
                )}
                title={request.decisionNote}
              >
                {request.status === 'REJECTED'
                  ? `Rejected: ${request.decisionNote}`
                  : request.decisionNote}
              </p>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-4 pt-1.5">
      {/* --- the session, the window and the action -------------------------- */}
      {!hideYearSelector && (
        <div className="card flex flex-col gap-4 p-4">
          {!isOwn && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="flex items-center gap-2 text-base font-[560] text-ink">
                <Clock className="h-4 w-4 text-accent" aria-hidden />
                {`${staffName}'s leave`}
              </h3>
              <Button
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => setIsFormOpen(true)}
                disabled={isLoading || Boolean(error) || isPastYear}
                title={isPastYear ? 'Leave is applied for in the current academic year' : undefined}
                className="w-full shrink-0 sm:w-auto"
              >
                {recordLabel}
              </Button>
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-[180px] w-full sm:w-72">
              <Select
                label="Academic year"
                options={(data?.academicYears ?? []).map((year) => ({
                  value: year.isCurrent ? '' : year.uuid,
                  label: year.isCurrent ? `${year.name} (current)` : year.name,
                }))}
                value={yearUuid}
                disabled={!data}
                onChange={(event) => chooseYear(event.target.value)}
              />
            </div>
          </div>

          {isPastYear && data && (
            <p className="text-xs text-ink-3">
              Showing {data.academicYear.name}, a past academic year. New leave is applied for in the
              current year.
            </p>
          )}
        </div>
      )}

      {hideYearSelector && isPastYear && data && (
        <div className="card p-3 bg-amber-500/10 border-amber-500/30 text-xs text-amber-700 dark:text-amber-400">
          Showing {data.academicYear.name}, a past academic year. New leave is applied for in the current year.
        </div>
      )}

      {error && (
        <QueryError title="Could not load this leave record" error={error} onRetry={refetch} />
      )}

      {isLoading && (
        <div className="flex flex-col gap-4" aria-busy="true">
          <span className="sr-only">Loading the leave record for {staffName}</span>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton key={index} className="h-[120px] rounded-2xl" />
            ))}
          </div>
          <Skeleton className="h-52 rounded-2xl" />
        </div>
      )}

      {!isLoading && !error && data && (
        <>
          {/* --- the balance, one card per kind of leave ------------------------ */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3 p-0.5">
            {data.balances.map((balance) => {
              const usedShare =
                balance.allowed === 0
                  ? 0
                  : Math.min(Math.round((balance.used / balance.allowed) * 100), 100);

              /* Per-type accent colours */
              const accent: Record<
                string,
                { bg: string; border: string; dot: string; bar: string; text: string }
              > = {
                CASUAL: {
                  bg: 'bg-indigo-50/80 dark:bg-indigo-500/10',
                  border: 'border-indigo-200 dark:border-indigo-500/30',
                  dot: 'bg-indigo-600 dark:bg-indigo-400',
                  bar: 'bg-indigo-500',
                  text: 'text-indigo-600 dark:text-indigo-400',
                },
                SICK: {
                  bg: 'bg-amber-50/80 dark:bg-amber-500/10',
                  border: 'border-amber-200 dark:border-amber-500/30',
                  dot: 'bg-amber-500',
                  bar: 'bg-amber-400',
                  text: 'text-amber-600 dark:text-amber-400',
                },
                PRIVILEGE: {
                  bg: 'bg-emerald-50/80 dark:bg-emerald-500/10',
                  border: 'border-emerald-200 dark:border-emerald-500/30',
                  dot: 'bg-emerald-500',
                  bar: 'bg-emerald-500',
                  text: 'text-emerald-600 dark:text-emerald-400',
                },
              };
              const a = accent[balance.type] ?? accent.CASUAL;

              return (
                <div
                  key={balance.type}
                  className={cn(
                    'relative flex flex-col justify-between gap-3 overflow-hidden rounded-[16px] border py-3.5 px-4 shadow-2xs transition-shadow hover:shadow-sm',
                    a.bg,
                    a.border,
                  )}
                >
                  {/* Header row */}
                  <div className="flex items-start justify-between gap-2.5">
                    <div className="min-w-0">
                      <p className={cn('text-base font-bold tracking-wide', a.text)}>
                        {STAFF_LEAVE_LABEL[balance.type]}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-ink-3" title={STAFF_LEAVE_HINT[balance.type]}>
                        {STAFF_LEAVE_HINT[balance.type]}
                      </p>
                    </div>
                    {/* Colour dot accent */}
                    <span className={cn('mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full shadow-xs', a.dot)} aria-hidden />
                  </div>

                  {/* Big number */}
                  <div>
                    <p className="flex items-baseline gap-1.5">
                      <span className="text-3xl font-[700] tabular-nums text-ink leading-none">
                        {balance.remaining}
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-ink-3">/ {balance.allowed} days</span>
                    </p>
                    <p className="mt-0.5 text-xs text-ink-3">
                      {balance.used > 0 ? `${balance.used} day${balance.used === 1 ? '' : 's'} used` : 'None used yet'}
                    </p>
                  </div>

                  {/* Progress bar */}
                  <div>
                    <div
                      className="h-1.5 w-full overflow-hidden rounded-full bg-black/10 dark:bg-white/10"
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={usedShare}
                      aria-label={
                        balance.allowed === 0
                          ? `${STAFF_LEAVE_LABEL[balance.type]}: no allowance`
                          : `${STAFF_LEAVE_LABEL[balance.type]}: ${usedShare}% used`
                      }
                    >
                      <div
                        className={cn('h-full rounded-full transition-all', a.bar)}
                        style={{ width: `${usedShare}%` }}
                      />
                    </div>
                    {/* With nothing allowed there is no share to speak of — "100%
                        remaining" of nought days would be a figure made up. */}
                    <p className="mt-1 flex justify-between text-2xs sm:text-xs text-ink-3">
                      <span>{balance.allowed === 0 ? 'No allowance' : `${balance.used} used`}</span>
                      <span>{balance.allowed === 0 ? '—' : `${balance.remaining} remaining`}</span>
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* --- the log --------------------------------------------------------- */}
          <div className="overflow-hidden rounded-[18px] border border-line bg-surface shadow-2xs">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8 border-b border-line bg-surface-2/50 px-5 py-3 sm:py-4">
              <div className="shrink-0">
                <h4 className="text-base font-bold text-ink">Leave Applications</h4>
                <p className="mt-0.5 text-xs text-ink-3">
                  {data.academicYear.name}
                  {hasDateFilter &&
                    ` · ${from ? formatDate(from) : 'start of year'} – ${to ? formatDate(to) : 'today'}`}
                </p>
              </div>

              {/* Status Tabs first, then Date Filters on the right corner */}
              <div className="flex flex-1 flex-wrap items-center justify-between gap-3 min-w-0">
                {/* 1. Status Filter Tabs (placed first, single-layer pills, highlighted with bg & text color, increased font size) */}
                <div
                  role="tablist"
                  aria-label="Filter leave applications by status"
                  className="flex items-center gap-2 shrink-0 overflow-x-auto no-scrollbar py-0.5"
                >
                  {(
                    [
                      {
                        key: 'ALL',
                        label: 'All',
                        count: counts.ALL,
                        activeBg: 'bg-indigo-600 text-white shadow-xs',
                      },
                      {
                        key: 'PENDING',
                        label: 'Pending',
                        count: counts.PENDING,
                        dot: 'bg-amber-500',
                        activeBg: 'bg-amber-500 text-white shadow-xs',
                      },
                      {
                        key: 'APPROVED',
                        label: 'Approved',
                        count: counts.APPROVED,
                        dot: 'bg-emerald-500',
                        activeBg: 'bg-emerald-600 text-white shadow-xs',
                      },
                      {
                        key: 'REJECTED',
                        label: 'Rejected',
                        count: counts.REJECTED,
                        dot: 'bg-rose-500',
                        activeBg: 'bg-rose-600 text-white shadow-xs',
                      },
                      {
                        key: 'CANCELLED',
                        label: 'Withdrawn',
                        count: counts.CANCELLED,
                        dot: 'bg-slate-400',
                        activeBg: 'bg-slate-600 text-white shadow-xs',
                      },
                    ] as const
                  ).map((tab) => {
                    const isSelected = statusFilter === tab.key;
                    return (
                      <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={isSelected}
                        onClick={() => setFilters({ status: tab.key })}
                        className={cn(
                          'inline-flex items-center gap-2 rounded-pill px-3.5 py-1.5 text-sm transition-all cursor-pointer select-none whitespace-nowrap',
                          isSelected
                            ? cn('font-bold border border-transparent', tab.activeBg)
                            : 'border border-line bg-surface hover:bg-surface-2 text-ink-2 hover:text-ink font-semibold shadow-2xs',
                        )}
                      >
                        {'dot' in tab && tab.dot && (
                          <span
                            className={cn(
                              'h-2 w-2 rounded-full shrink-0',
                              isSelected ? 'bg-white' : tab.dot,
                            )}
                            aria-hidden
                          />
                        )}
                        <span>{tab.label}</span>
                        <span
                          className={cn(
                            'px-2 py-0.5 rounded-full text-xs font-bold tabular-nums',
                            isSelected
                              ? 'bg-white/25 text-white'
                              : 'bg-surface-2 border border-line text-ink-3',
                          )}
                        >
                          {tab.count}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* 2. Date Filters (placed after, on the right corner) */}
                <div className="flex items-center gap-2 ml-auto shrink-0">
                  <div className="w-32 sm:w-36">
                    <DatePicker
                      label="From"
                      hideLabel
                      size="sm"
                      placeholder="From"
                      value={from}
                      max={to || undefined}
                      isClearable
                      onChange={(event) => setFilters({ from: event.target.value })}
                      className="h-8 text-sm font-semibold rounded-field"
                    />
                  </div>
                  <div className="w-32 sm:w-36">
                    <DatePicker
                      label="To"
                      hideLabel
                      size="sm"
                      placeholder="To"
                      value={to}
                      min={from || undefined}
                      isClearable
                      onChange={(event) => setFilters({ to: event.target.value })}
                      className="h-8 text-sm font-semibold rounded-field"
                    />
                  </div>
                  {hasDateFilter && (
                    <button
                      type="button"
                      onClick={() => {
                        setFilters({ from: '', to: '' });
                      }}
                      className="inline-flex items-center justify-center gap-1 h-8 px-2.5 rounded-field text-xs font-semibold bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 hover:border-rose-300 dark:bg-rose-500/15 dark:text-rose-400 dark:border-rose-500/30 dark:hover:bg-rose-500/25 shadow-xs transition-colors cursor-pointer select-none"
                      title="Clear date filters"
                    >
                      <X className="h-3.5 w-3.5 shrink-0" />
                      <span>Clear</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            <Table
              variant="plain"
              columns={columns}
              rows={requests}
              rowKey={(request) => request.uuid}
              empty={
                hasDateFilter || statusFilter !== 'ALL' ? (
                  <EmptyState
                    icon={<CalendarRange className="h-5 w-5" />}
                    title="No leave matching filters"
                    description="Nothing was found matching the chosen dates or status filter. Widen the dates, switch tabs, or clear the filters."
                  />
                ) : (
                  <EmptyState
                    icon={<Clock className="h-5 w-5" />}
                    title={`No leave in ${data.academicYear.name}`}
                    description={
                      isOwn
                        ? `You have not applied for any leave in ${data.academicYear.name}.`
                        : `Nothing has been recorded against ${staffName} for ${data.academicYear.name}.`
                    }
                    action={
                      isPastYear ? undefined : (
                        <Button size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setIsFormOpen(true)}>
                          {recordLabel}
                        </Button>
                      )
                    }
                  />
                )
              }
            />
          </div>
        </>
      )}

      <StaffLeaveModal
        isOpen={isFormOpen}
        staffName={staffName}
        onClose={() => setIsFormOpen(false)}
        onSave={handleSave}
        isOwn={isOwn}
        balances={isOwn ? (data?.balances ?? undefined) : undefined}
      />

      <ConfirmDialog
        isOpen={Boolean(withdrawing)}
        tone="danger"
        title="Withdraw this leave request?"
        description="It stays on the log marked as withdrawn, and stops counting against the balance."
        confirmLabel="Withdraw it"
        isLoading={isWithdrawing}
        onConfirm={confirmWithdraw}
        onCancel={() => setWithdrawing(null)}
      />
    </div>
  );
}
